import { useQuery } from '@apollo/client'
import { Box, styled } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { UserProfileDocument } from '../../../../graphql/generated'
import useBucket from '../../hooks/useBucket'
import AlbumDeletePillButton from './pillActions/AlbumDeletePillButton'
import AlbumDetailsPillButton from './pillActions/AlbumDetailsPillButton'
import AlbumDownloadPillButton from './pillActions/AlbumDownloadPillButton'
import AlbumOrganizeActionPill from './pillActions/AlbumOrganizeActionPill'

// Styles for descendants of the listItem
export const listItemSx = {
  '& .MuiListItemIcon-root': { minWidth: '36px' },
  '& .MuiListItemButton-root': { borderRadius: '4px', padding: '8px 12px' }
}

const PillWrapper = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: 'fixed',
  bottom: '16px',
  right: '32px',
  backgroundColor: '#1C47CB',
  borderRadius: '100px',
  display: 'flex',
  flexDirection: 'row',
  gap: '32px',
  zIndex: 1,
  animation: 'rotateInVerticalSmooth 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
  '@keyframes rotateInVerticalSmooth': {
    '0%': {
      opacity: 0,
      transform: 'perspective(600px) rotateX(90deg) translateX(0)'
    },
    '100%': {
      opacity: 1,
      transform: 'perspective(600px) rotateX(0deg) translateX(0)'
    }
  },
  [theme.breakpoints.down('sm')]: {
    bottom: '8px',
    right: 'auto',
    left: '50%',
    transform: 'translateX(-50%)',
    margin: '0 auto',
    maxWidth: 'calc(100% - 16px)',
    gap: 16,
    justifyContent: 'space-between',
    // Override animation for mobile to prevent skewing off screen
    animation: 'rotateInVerticalSmoothMobile 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
    '@keyframes rotateInVerticalSmoothMobile': {
      '0%': {
        opacity: 0,
        transform: 'translateX(-50%) perspective(600px) rotateX(90deg)'
      },
      '100%': {
        opacity: 1,
        transform: 'translateX(-50%) perspective(600px) rotateX(0deg)'
      }
    }
  }
}))

// ActionButton is now replaced by individual Pill* components

const ROUTES = {
  bucket: (id: string | undefined) => `/bucket/${id}`,
  albums: (id: string | undefined) => `/bucket/${id}/albums`,
  people: (id: string | undefined) => `/bucket/${id}/manage`, // remove when sharing page is enabled
  sharing: (id: string | undefined) => `/bucket/${id}/sharing`,
  trash: (id: string | undefined) => `/bucket/${id}/trash`
}

// type RouteKey = keyof typeof ROUTES

export default function AlbumActionPill() {
  const { bucket } = useBucket()
  const { data } = useQuery(UserProfileDocument)
  const bucketId = bucket?.id ? bucket?.id : data?.userProfile?.currentBucketId || ''
  const location = useLocation()
  const isCurrentRoute = useMemo(() => {
    return (route: string) => location.pathname === route
  }, [location])

  if (!bucketId) {
    return null
  }

  if (isCurrentRoute(ROUTES.trash(bucketId))) {
    return null
  }

  return (
    <PillWrapper sx={{ padding: '16px 32px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)' }}>
      <AlbumDetailsPillButton />
      <AlbumDownloadPillButton />
      <AlbumDeletePillButton />
      <AlbumOrganizeActionPill />
    </PillWrapper>
  )
}
